import CurrencyBadgeUi from 'common/components/entities/OfferPriceNew/ui/CurrencyBadgeUi'
import DividerUi from 'common/components/entities/OfferPriceNew/ui/DividerUi'
import PricePlanAmountUi from 'common/components/entities/OfferPriceNew/ui/PricePlanAmountUi'
import PricePlanItemUi from 'common/components/entities/OfferPriceNew/ui/PricePlanItemUi'
import PricePlanUi from 'common/components/entities/OfferPriceNew/ui/PricePlanUi'
import { centsToEuro, getReadablePrice } from 'common/utils/price-calculator'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import PricePlanNameUi from '../ui/PricePlanNameUi'

interface ShippingSummaryProps {
  finalAmount: number
  productPricing?: CalculatePriceResponse
  amountFontFamily?: string
  locale: string
  mobileAmountFontFamily?: string
  amountFontSize: number
  mobileAmountFontSize?: number
  amountFontStyle?: string
  mobileAmountFontStyle?: string
  amountFontWeight?: string
  mobileAmountFontWeight?: string
  amountLineHeight: number
  mobileAmountLineHeight?: number
  amountColor: string
  mobileAmountColor?: string
  fontFamily?: string
  mobileFontFamily?: string
  fontSize: number
  mobileFontSize?: number
  fontStyle?: string
  mobileFontStyle?: string
  fontWeight?: string
  mobileFontWeight?: string
  lineHeight: number
  mobileLineHeight?: number
  color: string
  mobileColor?: string
}

const ShippingSummary = ({
  finalAmount,
  productPricing,
  locale,
  amountFontSize,
  mobileAmountFontSize,
  amountFontStyle,
  mobileAmountFontStyle,
  amountFontWeight,
  mobileAmountFontWeight,
  amountFontFamily,
  mobileAmountFontFamily,
  amountLineHeight,
  mobileAmountLineHeight,
  amountColor,
  mobileAmountColor,
  fontSize,
  mobileFontSize,
  fontStyle,
  mobileFontStyle,
  fontWeight,
  mobileFontWeight,
  fontFamily,
  mobileFontFamily,
  lineHeight,
  mobileLineHeight,
  color,
  mobileColor,
}: ShippingSummaryProps) => {
  const { t } = useTypedTranslation(['common', 'publisher'])

  return (
    <>
      <DividerUi />
      <PricePlanUi>
        <PricePlanItemUi skipLeftOffset>
          <PricePlanNameUi
            fontSize={fontSize}
            mobileFontSize={mobileFontSize}
            fontStyle={fontStyle}
            mobileFontStyle={mobileFontStyle}
            fontWeight={fontWeight}
            mobileFontWeight={mobileFontWeight}
            fontFamily={fontFamily}
            mobileFontFamily={mobileFontFamily}
            lineHeight={lineHeight}
            mobileLineHeight={mobileLineHeight}
            color={color}
            mobileColor={mobileColor}
            data-test-id="offer-price-shipping-summary-net"
          >
            {t('components.offer_price.shipping')}
          </PricePlanNameUi>
          <PricePlanAmountUi
            fontSize={amountFontSize}
            mobileFontSize={mobileAmountFontSize}
            fontStyle={amountFontStyle}
            mobileFontStyle={mobileAmountFontStyle}
            fontWeight={amountFontWeight}
            mobileFontWeight={mobileAmountFontWeight}
            fontFamily={amountFontFamily}
            mobileFontFamily={mobileAmountFontFamily}
            lineHeight={amountLineHeight}
            mobileLineHeight={mobileAmountLineHeight}
            color={amountColor}
            mobileColor={mobileAmountColor}
          >
            <CurrencyBadgeUi
              color={amountColor}
              mobileColor={mobileAmountColor}
            >
              {productPricing?.currencyCode}
            </CurrencyBadgeUi>

            {getReadablePrice(
              centsToEuro(productPricing?.shippingFee.amount ?? 0),
              productPricing?.currencyCode ?? '',
              locale,
            )}
          </PricePlanAmountUi>
        </PricePlanItemUi>
        {(productPricing?.shippingFee.tax.amount ?? 0) > 0 && (
          <PricePlanItemUi skipLeftOffset>
            <PricePlanNameUi
              fontSize={fontSize}
              mobileFontSize={mobileFontSize}
              fontStyle={fontStyle}
              mobileFontStyle={mobileFontStyle}
              fontWeight={fontWeight}
              mobileFontWeight={mobileFontWeight}
              fontFamily={fontFamily}
              mobileFontFamily={mobileFontFamily}
              lineHeight={lineHeight}
              mobileLineHeight={mobileLineHeight}
              color={color}
              mobileColor={mobileColor}
              data-test-id="offer-price-shipping-summary-tax"
            >
              {`${t('components.offer_price.vat')} (${
                productPricing?.shippingFee.tax.rate
              }%)`}
            </PricePlanNameUi>
            <PricePlanAmountUi
              fontSize={amountFontSize}
              mobileFontSize={mobileAmountFontSize}
              fontStyle={amountFontStyle}
              mobileFontStyle={mobileAmountFontStyle}
              fontWeight={amountFontWeight}
              mobileFontWeight={mobileAmountFontWeight}
              fontFamily={amountFontFamily}
              mobileFontFamily={mobileAmountFontFamily}
              lineHeight={amountLineHeight}
              mobileLineHeight={mobileAmountLineHeight}
              color={amountColor}
              mobileColor={mobileAmountColor}
            >
              <CurrencyBadgeUi
                color={amountColor}
                mobileColor={mobileAmountColor}
              >
                {productPricing?.currencyCode}
              </CurrencyBadgeUi>

              {getReadablePrice(
                centsToEuro(productPricing?.shippingFee.tax.amount ?? 0),
                productPricing?.currencyCode ?? '',
                locale,
              )}
            </PricePlanAmountUi>
          </PricePlanItemUi>
        )}
        <DividerUi />
        <PricePlanItemUi skipLeftOffset>
          <PricePlanNameUi
            fontSize={fontSize}
            mobileFontSize={mobileFontSize}
            fontStyle={fontStyle}
            mobileFontStyle={mobileFontStyle}
            fontWeight={fontWeight}
            mobileFontWeight={mobileFontWeight}
            fontFamily={fontFamily}
            mobileFontFamily={mobileFontFamily}
            lineHeight={lineHeight}
            mobileLineHeight={mobileLineHeight}
            color={color}
            mobileColor={mobileColor}
            data-test-id="offer-price-shipping-summary-total"
          >
            {t(
              'components.offer_price.one_shots_summary.total_amount_with_tax_and_shipping',
            )}
          </PricePlanNameUi>
          <PricePlanAmountUi
            fontSize={amountFontSize}
            mobileFontSize={mobileAmountFontSize}
            fontStyle={amountFontStyle}
            mobileFontStyle={mobileAmountFontStyle}
            fontWeight={amountFontWeight}
            mobileFontWeight={mobileAmountFontWeight}
            fontFamily={amountFontFamily}
            mobileFontFamily={mobileAmountFontFamily}
            lineHeight={amountLineHeight}
            mobileLineHeight={mobileAmountLineHeight}
            color={amountColor}
            mobileColor={mobileAmountColor}
          >
            <CurrencyBadgeUi
              color={amountColor}
              mobileColor={mobileAmountColor}
            >
              {productPricing?.currencyCode}
            </CurrencyBadgeUi>

            {getReadablePrice(
              centsToEuro(finalAmount),
              productPricing?.currencyCode ?? '',
              locale,
            )}
          </PricePlanAmountUi>
        </PricePlanItemUi>
      </PricePlanUi>
    </>
  )
}

export default ShippingSummary
